import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, authHeaders } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Ticket, Search, Send, Loader2, MessageSquare, CheckCircle2, Download, ChevronDown, Gift } from "lucide-react";
import { relativeDate, euro } from "@/lib/format";
import { couponBadge } from "@/pages/vendor/VendorCoupons";
import { toast } from "sonner";

const tplLabel = (t) => `${t.title || "Coupon"} · ${couponBadge(t)}`;

export default function VendorCouponClients() {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [totals, setTotals] = useState({ down: 0, used: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [openOffer, setOpenOffer] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [target, setTarget] = useState(null);
  const [bulk, setBulk] = useState(null);
  const [text, setText] = useState("");
  const [tplCoupon, setTplCoupon] = useState("none");
  const [sending, setSending] = useState(false);

  const unusedClientIds = new Set();
  offers.forEach((o) => o.clients.forEach((c) => { if (!c.used) unusedClientIds.add(c.client_account_id); }));
  const unusedCount = unusedClientIds.size;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, t] = await Promise.all([
        api.get("/vendor/coupon-clients", { headers: authHeaders("vendor") }),
        api.get("/vendor/coupon-templates", { headers: authHeaders("vendor") }),
      ]);
      setOffers(r.data.offers || []);
      setTotals({ down: r.data.total_downloaded || 0, used: r.data.total_used || 0 });
      setTemplates(t.data.templates || []);
    } catch (_) { toast.error("Errore nel caricamento"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openMessage = (c) => { setBulk(null); setTarget(c); setText(""); setTplCoupon("none"); };
  const openBulk = (offer) => {
    setTarget(null);
    setBulk(offer ? { offer_id: offer.offer_id, count: offer.clients.filter((c) => !c.used).length, title: offer.title }
                  : { offer_id: null, count: unusedCount });
    setText(""); setTplCoupon("none");
  };
  const sendMessage = async () => {
    if (!text.trim() && tplCoupon === "none") return toast.error("Scrivi un messaggio o allega un coupon");
    setSending(true);
    try {
      await api.post(`/vendor/clients/${target.client_account_id}/message`, { text: text.trim(), coupon_template_id: tplCoupon === "none" ? null : tplCoupon }, { headers: authHeaders("vendor") });
      toast.success(`Messaggio inviato a ${target.name}`);
      setTarget(null);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore invio"); }
    finally { setSending(false); }
  };
  const sendBulk = async () => {
    setSending(true);
    try {
      const r = await api.post("/vendor/coupon-clients/remind-unused", { offer_id: bulk.offer_id, text: text.trim() || null, coupon_template_id: tplCoupon === "none" ? null : tplCoupon }, { headers: authHeaders("vendor") });
      toast.success(`Messaggio inviato a ${r.data.sent} clienti`);
      setBulk(null);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore invio"); }
    finally { setSending(false); }
  };
  const remindQuick = async (offer) => {
    const n = offer ? offer.clients.filter((c) => !c.used).length : unusedCount;
    if (!window.confirm(`Inviare un promemoria a ${n} cliente/i che hanno scaricato ma non usato${offer ? " questo coupon" : ""}?`)) return;
    try {
      const r = await api.post("/vendor/coupon-clients/remind-unused", { offer_id: offer ? offer.offer_id : null }, { headers: authHeaders("vendor") });
      toast.success(`Promemoria inviato a ${r.data.sent} clienti`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore invio"); }
  };

  const matchClient = (c) => {
    const s = q.trim().toLowerCase();
    if (s && !(c.name || "").toLowerCase().includes(s)) return false;
    if (statusFilter === "used" && !c.used) return false;
    if (statusFilter === "downloaded" && c.used) return false;
    return true;
  };

  return (
    <div className="space-y-6 kn-fade-up max-w-3xl" data-testid="vendor-coupon-clients-page">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900">Coupon dei clienti</h1>
        <p className="text-slate-500">Chi ha scaricato e chi ha usato i tuoi coupon. Scrivi loro direttamente (in-app).</p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="kn-card px-3 py-2.5 flex items-center gap-2.5" data-testid="stat-downloaded">
          <Download className="h-4 w-4 text-slate-400 shrink-0" />
          <div><p className="text-lg font-extrabold text-slate-900 leading-none">{totals.down}</p><p className="text-xs text-slate-500 mt-1">Scaricati</p></div>
        </div>
        <div className="kn-card px-3 py-2.5 flex items-center gap-2.5" data-testid="stat-used">
          <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
          <div><p className="text-lg font-extrabold text-slate-900 leading-none">{totals.used}</p><p className="text-xs text-slate-500 mt-1">Usati</p></div>
        </div>
      </div>

      {!loading && unusedCount > 0 && (
        <div className="flex flex-col sm:flex-row gap-2" data-testid="bulk-actions">
          <Button onClick={() => remindQuick(null)} variant="outline" data-testid="remind-all-btn" className="flex-1 h-11 rounded-xl font-bold gap-2 border-amber-200 text-amber-700 hover:bg-amber-50">
            <Send className="h-4 w-4" /> Promemoria a tutti ({unusedCount})
          </Button>
          <Button onClick={() => openBulk(null)} data-testid="bulk-message-btn" className="flex-1 h-11 rounded-xl font-bold gap-2">
            <MessageSquare className="h-4 w-4" /> Messaggio massivo
          </Button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente per nome..." className="h-12 pl-11 rounded-xl" data-testid="coupon-clients-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-12 rounded-xl sm:w-48" data-testid="coupon-clients-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            <SelectItem value="downloaded">Solo scaricati (non usati)</SelectItem>
            <SelectItem value="used">Solo usati</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#E63946]" /></div>
      ) : offers.length === 0 ? (
        <div className="kn-card p-10 text-center" data-testid="coupon-clients-empty">
          <div className="h-14 w-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3"><Ticket className="h-7 w-7 text-slate-400" /></div>
          <p className="font-bold text-slate-700">Ancora nessun coupon scaricato</p>
          <p className="text-sm text-slate-500 mt-1">Quando i clienti scaricheranno le tue offerte, li vedrai qui.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {offers.map((o) => {
            const clients = o.clients.filter(matchClient);
            const isOpen = openOffer === (o.offer_id || o.title);
            if (q.trim() && clients.length === 0) return null;
            return (
              <div key={o.offer_id || o.title} className="kn-card overflow-hidden" data-testid={`coupon-offer-${o.offer_id || "grp"}`}>
                <button onClick={() => setOpenOffer(isOpen ? null : (o.offer_id || o.title))} className="w-full p-4 flex items-center gap-3 text-left">
                  <div className="h-11 w-11 rounded-xl bg-[#FFE4E6] flex items-center justify-center shrink-0"><Ticket className="h-5 w-5 text-[#E63946]" /></div>
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-slate-800 truncate">{o.title}</p>
                    <p className="text-xs text-slate-400"><b className="text-slate-600">{o.downloaded}</b> scaricati · <b className="text-emerald-600">{o.used}</b> usati</p>
                  </div>
                  {o.downloaded - o.used > 0 && (
                    <span onClick={(e) => { e.stopPropagation(); remindQuick(o); }} role="button" data-testid={`remind-offer-${o.offer_id || "grp"}`}
                      className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 shrink-0 flex items-center gap-1">
                      <Send className="h-3 w-3" /> Sollecita {o.downloaded - o.used}
                    </span>
                  )}
                  <ChevronDown className={`h-5 w-5 text-slate-300 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="border-t border-slate-100 divide-y divide-slate-50">
                    {clients.length === 0 ? (
                      <p className="p-4 text-sm text-slate-400 text-center">Nessun cliente con questo filtro.</p>
                    ) : clients.map((c) => (
                      <div key={c.claim_id} className="p-3.5 flex items-center gap-3" data-testid={`coupon-client-${c.claim_id}`}>
                        <div className="h-10 w-10 rounded-full bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
                          {c.photo ? <img src={c.photo} alt="" className="h-full w-full object-cover" /> : <span className="font-extrabold text-slate-400">{(c.name || "?").charAt(0).toUpperCase()}</span>}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-bold text-slate-800 truncate">{c.name}</p>
                          <p className="text-xs text-slate-400 truncate">
                            {c.used
                              ? <>Usato {c.redeemed_at ? relativeDate(c.redeemed_at) : ""}{c.amount_spent ? ` · ${euro(c.amount_spent)}` : ""}</>
                              : <>Scaricato {c.claimed_at ? relativeDate(c.claimed_at) : ""}</>}
                          </p>
                        </div>
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full shrink-0 ${c.used ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{c.used ? "Usato" : "Scaricato"}</span>
                        <button onClick={() => openMessage(c)} title="Invia messaggio" data-testid={`message-client-${c.claim_id}`}
                          className="h-9 w-9 rounded-lg flex items-center justify-center text-[#E63946] hover:bg-[#FFF1F2] shrink-0"><MessageSquare className="h-4 w-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!target || !!bulk} onOpenChange={(v) => { if (!v) { setTarget(null); setBulk(null); } }}>
        <DialogContent className="sm:max-w-md rounded-3xl" data-testid="client-message-dialog">
          <DialogHeader><DialogTitle className="text-xl font-extrabold">{bulk ? `Messaggio a ${bulk.count} clienti` : `Messaggio a ${target?.name}`}</DialogTitle>
            <DialogDescription className="text-slate-500">{bulk ? "Verrà inviato a chi ha scaricato ma non ancora usato il coupon. Solo in-app, contatti privati." : "Il messaggio arriva nella chat in-app del cliente. I dati di contatto restano privati."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3.5">
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={1000}
              placeholder={bulk ? "Scrivi un messaggio per tutti (vuoto = promemoria automatico)..." : "Scrivi un messaggio a questo cliente..."} className="rounded-xl" data-testid="client-message-text" />
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5"><Ticket className="h-3.5 w-3.5" /> Allega un coupon (facoltativo)</Label>
              <Select value={tplCoupon} onValueChange={setTplCoupon}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="client-coupon-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun coupon</SelectItem>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{tplLabel(t)}</SelectItem>)}
                </SelectContent>
              </Select>
              {templates.length === 0 && <button type="button" onClick={() => navigate("/vendor/coupons")} className="text-[11px] text-[#E63946] font-semibold underline flex items-center gap-1"><Gift className="h-3 w-3" /> Crea prima un coupon predefinito</button>}
            </div>
            <Button onClick={bulk ? sendBulk : sendMessage} disabled={sending || (!bulk && !text.trim() && tplCoupon === "none")} data-testid="client-message-send" className="w-full h-12 rounded-xl font-bold gap-2">
              {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />} {bulk ? `Invia a ${bulk.count} clienti` : "Invia messaggio"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
